import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { CartItem } from '@rosita/shared';
import { api } from '../lib/api';

interface AppliedCoupon {
  code: string;
  discountAmount: number;
  freeShipping: boolean;
}

interface CartState {
  items: CartItem[];
  couponCode: string | null;
  appliedCoupon: AppliedCoupon | null;
  couponError: string | null;

  addItem: (item: CartItem) => void;
  removeItem: (variantId: string) => void;
  updateQuantity: (variantId: string, quantity: number) => void;
  clearCart: () => void;
  getSubtotal: () => number;
  getItemCount: () => number;
  getTotalWeightGrams: (weightByVariant: Record<string, number>) => number;
  applyCoupon: (code: string) => Promise<boolean>;
  removeCoupon: () => void;
}

interface CouponValidateResponse {
  valid: boolean;
  coupon?: { code: string };
  discountAmount?: number;
  freeShipping?: boolean;
  error?: string;
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      couponCode: null,
      appliedCoupon: null,
      couponError: null,

      addItem: (item) =>
        set((state) => {
          const existing = state.items.find((i) => i.variantId === item.variantId);
          if (existing) {
            const maxStock = item.maxStock;
            const nextQty = Math.min(existing.quantity + item.quantity, maxStock);
            return {
              items: state.items.map((i) =>
                i.variantId === item.variantId ? { ...i, quantity: nextQty } : i
              ),
            };
          }
          return { items: [...state.items, item] };
        }),

      removeItem: (variantId) =>
        set((state) => ({ items: state.items.filter((i) => i.variantId !== variantId) })),

      updateQuantity: (variantId, quantity) =>
        set((state) => ({
          items: state.items
            .map((i) =>
              i.variantId === variantId
                ? { ...i, quantity: Math.max(0, Math.min(quantity, i.maxStock)) }
                : i
            )
            .filter((i) => i.quantity > 0),
        })),

      clearCart: () => set({ items: [], couponCode: null, appliedCoupon: null, couponError: null }),

      getSubtotal: () => {
        return get().items.reduce((sum, i) => sum + i.price * i.quantity, 0);
      },

      getItemCount: () => get().items.reduce((n, i) => n + i.quantity, 0),

      getTotalWeightGrams: (weightByVariant) =>
        get().items.reduce((sum, i) => {
          const w = weightByVariant[i.variantId] ?? 0;
          return sum + w * i.quantity;
        }, 0),

      applyCoupon: async (code) => {
        const subtotal = get().getSubtotal();
        try {
          const response = await api.post<CouponValidateResponse>('/coupons/validate', {
            code,
            subtotal,
          });
          const result = response.data;
          if (!result.valid || !result.coupon) {
            set({ appliedCoupon: null, couponError: result.error ?? 'Cupão inválido' });
            return false;
          }
          set({
            couponCode: code,
            appliedCoupon: {
              code: result.coupon.code,
              discountAmount: result.discountAmount ?? 0,
              freeShipping: result.freeShipping ?? false,
            },
            couponError: null,
          });
          return true;
        } catch {
          set({ appliedCoupon: null, couponError: 'Erro ao validar o cupão' });
          return false;
        }
      },

      removeCoupon: () =>
        set({ couponCode: null, appliedCoupon: null, couponError: null }),
    }),
    {
      name: 'rosita.cart',
      partialize: (s) => ({
        items: s.items,
        couponCode: s.couponCode,
        appliedCoupon: s.appliedCoupon,
      }),
    }
  )
);
